import React, { useState, useEffect } from 'react';
import './DashboardPage.css';
import apiService from '../../services/apiService';
import { useNavigate, Link } from 'react-router-dom';
import ZoneList from '../../components/admin/ZoneList';
import ZoneForm from '../../components/admin/ZoneForm';
import QRCodeGenerator from '../../components/admin/QRCodeGenerator';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import StripeOnboardingMessage from '../../components/admin/StripeOnboardingMessage';
import Logo from '../../components/brand/Logo';
import Icon from '../../components/brand/Icon';

const PERMIT_PRICE = 5;

const DashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [zones, setZones] = useState([]);
  const [showAddZoneForm, setShowAddZoneForm] = useState(false);
  const [editingZone, setEditingZone] = useState(null);
  const [qrCodeZone, setQrCodeZone] = useState(null);
  const [deleteZoneId, setDeleteZoneId] = useState(null);
  const [error, setError] = useState(null);
  const [stripeAccountStatus, setStripeAccountStatus] = useState(null);
  const navigate = useNavigate();

  // TODO: Replace with live figures from the backend.
  const permitsSoldToday = 23;
  const activePermits = 18;
  const expiredToday = 5;
  const revenueToday = permitsSoldToday * PERMIT_PRICE;

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) { navigate('/admin/signin'); return; }
    loadDashboardData();
  }, [navigate]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const stripeResponse = await apiService.checkStripeAccountStatus();
      setStripeAccountStatus(stripeResponse.data?.status || 'incomplete');
      const zonesResponse = await apiService.getAllZones();
      setZones(zonesResponse.data.zones || []);
      setLoading(false);
    } catch (err) {
      setError('Failed to load dashboard data. Please try again.');
      setLoading(false);
    }
  };

  const handleSaveZone = async (zoneData) => {
    try {
      if (editingZone) {
        await apiService.updateZone(editingZone.id, zoneData);
      } else {
        await apiService.createZone(zoneData);
      }
      setShowAddZoneForm(false);
      setEditingZone(null);
      loadDashboardData();
    } catch (err) {
      throw err;
    }
  };

  const confirmDeleteZone = async () => {
    try {
      if (!deleteZoneId) return;
      await apiService.deleteZone(deleteZoneId);
      setDeleteZoneId(null);
      loadDashboardData();
    } catch (err) {
      setError(err.message || 'Failed to delete zone');
    }
  };

  const handleSignOut = async () => {
    try {
      await apiService.signOut();
      navigate('/admin/signin');
    } catch (err) {
      console.error('Error signing out:', err);
    }
  };

  if (loading) {
    return (
      <div className="adm-loading">
        <div className="adm-loading-spinner"></div>
        Loading dashboard...
      </div>
    );
  }

  const isStripeSetupComplete = stripeAccountStatus === 'complete';

  return (
    <div className="adm-page">
      <header className="adm-header">
        <div className="adm-header-inner">
          <div className="adm-brand">
            <Logo size="md" />
            <div>
              <div className="adm-brand-name">Business Portal</div>
              <div className="adm-brand-sub">Space Monkey · Edinburg, TX</div>
            </div>
          </div>
          <div className="adm-header-actions">
            <Link to="/spacemonkey/dashboard" className="adm-link">Space Monkey</Link>
            <Link to="/enforcement" className="adm-link">Enforcement</Link>
            <button className="adm-signout" onClick={handleSignOut}>Sign Out</button>
          </div>
        </div>
      </header>

      <div className="adm-content">
        {error && <div className="adm-error">{error}</div>}

        {/* Revenue Stats */}
        <section className="adm-section">
          <h2 className="adm-section-title">Today's Overview</h2>
          <div className="adm-stats-grid">
            <div className="adm-stat-card green">
              <div className="adm-stat-icon"><Icon name="dollar" size={22} /></div>
              <div className="adm-stat-body">
                <div className="adm-stat-label">Today's Revenue</div>
                <div className="adm-stat-value">${revenueToday}</div>
                <div className="adm-stat-sub">{permitsSoldToday} permits at $5</div>
              </div>
            </div>
            <div className="adm-stat-card blue">
              <div className="adm-stat-icon"><Icon name="ticket" size={22} /></div>
              <div className="adm-stat-body">
                <div className="adm-stat-label">Permits Sold Today</div>
                <div className="adm-stat-value">{permitsSoldToday}</div>
                <div className="adm-stat-sub">$5 flat fee each</div>
              </div>
            </div>
            <div className="adm-stat-card teal">
              <div className="adm-stat-icon"><Icon name="check" size={22} /></div>
              <div className="adm-stat-body">
                <div className="adm-stat-label">Active Permits</div>
                <div className="adm-stat-value">{activePermits}</div>
                <div className="adm-stat-sub">Valid right now</div>
              </div>
            </div>
            <div className="adm-stat-card gray">
              <div className="adm-stat-icon"><Icon name="clock" size={22} /></div>
              <div className="adm-stat-body">
                <div className="adm-stat-label">Expired Today</div>
                <div className="adm-stat-value">{expiredToday}</div>
                <div className="adm-stat-sub">Ended at midnight</div>
              </div>
            </div>
          </div>
        </section>

        {/* Revenue Projections */}
        <section className="adm-section">
          <h2 className="adm-section-title">Your Revenue Projections (parking fee: $5 per permit)</h2>
          <div className="adm-projections">
            {[
              { label: 'Conservative', daily: 15, color: 'gray' },
              { label: 'Moderate', daily: 30, color: 'blue' },
              { label: 'Peak Lot', daily: 60, color: 'green' },
            ].map(r => (
              <div className={`adm-projection adm-projection-${r.color}`} key={r.label}>
                <div className="adm-proj-label">{r.label}</div>
                <div className="adm-proj-permits">{r.daily} permits/day</div>
                <div className="adm-proj-daily">${r.daily * PERMIT_PRICE}<span>/day</span></div>
                <div className="adm-proj-monthly">${(r.daily * PERMIT_PRICE * 30).toLocaleString()}/month</div>
              </div>
            ))}
          </div>
        </section>

        {/* Zone Management */}
        <section className="adm-section">
          <div className="adm-section-head">
            <h2 className="adm-section-title">Parking Zones</h2>
            {isStripeSetupComplete && !showAddZoneForm && !editingZone && (
              <button className="adm-add-btn" onClick={() => setShowAddZoneForm(true)}>+ Add Zone</button>
            )}
          </div>

          {!isStripeSetupComplete ? (
            <StripeOnboardingMessage />
          ) : showAddZoneForm || editingZone ? (
            <ZoneForm
              zone={editingZone}
              onSave={handleSaveZone}
              onCancel={() => { setShowAddZoneForm(false); setEditingZone(null); }}
            />
          ) : (
            <ZoneList
              zones={zones}
              onEditZone={setEditingZone}
              onDeleteZone={setDeleteZoneId}
              onGenerateQR={setQrCodeZone}
            />
          )}
        </section>
      </div>

      {qrCodeZone && <QRCodeGenerator zone={qrCodeZone} onClose={() => setQrCodeZone(null)} />}
      {deleteZoneId && (
        <ConfirmDialog
          title="Delete Zone"
          message="Are you sure you want to delete this zone? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={confirmDeleteZone}
          onCancel={() => setDeleteZoneId(null)}
        />
      )}
    </div>
  );
};

export default DashboardPage;
